import { useEffect } from 'react'
import { apiClient } from '@/api/client'
import { isBackendReachable, useOnlineAndReachable } from './reachability'

/**
 * Ask the backend whether it is there, on a timer, while we believe it is not
 * (F-FE-N71).
 *
 * WHY THIS EXISTS
 *
 * `reachability.ts` learns the backend is DOWN from any failed request, at the
 * single HTTP choke point. Learning it is back UP had no mechanism at all: the
 * flag cleared only as a side effect of some unrelated query happening to
 * succeed. Whether that ever happened depended entirely on what the mounted
 * screen was polling.
 *
 * On the counter it usually did — three queries poll at 15–60s — so the bug hid.
 * It surfaced on 2026-08-26 as "I reconnected the internet, waited two minutes,
 * and nothing happened", and the reason nothing happened is that nothing asked.
 *
 * That is not cosmetic, because two things ride the offline→online EDGE:
 *
 *   - the capability flip, so the cashier is told they are back, and
 *   - draining the offline queue.
 *
 * An edge that never fires means orders the kitchen has already cooked sit
 * unsynced with nothing on screen suggesting anything is wrong. Recovery must be
 * something the app DOES, not something it hopes for.
 *
 * Deliberately asymmetric: this polls only while the backend is believed down.
 * Polling while healthy would add fleet-wide load to prove something every other
 * request already proves, and `markBackendReachable()` fires from the choke point
 * on any success anyway (invariant #9), so the probe needs no success handler —
 * a 200 here recovers reachability through exactly the same path as a 200
 * anywhere else.
 */

/**
 * Fifteen seconds: fast enough that a cashier who has just plugged the router
 * back in sees the POS return before they would think to reload it, slow enough
 * that a whole restaurant's terminals reconnecting at once is not a stampede.
 * The endpoint touches no database precisely so this cadence is free.
 */
export const PROBE_INTERVAL_MS = 15_000

/**
 * One probe. Resolves true if the backend answered.
 *
 * Never throws: a probe that rejects while offline is the expected case, not an
 * error, and an unhandled rejection every 15 seconds would bury the console the
 * cashier's screenshots are taken from.
 */
export async function probeBackendOnce(): Promise<boolean> {
  try {
    await apiClient.ping()
    return true
  } catch {
    return false
  }
}

/**
 * Run the probe while the app believes the backend is unreachable.
 *
 * Mounted once, at the root, rather than in the counter: every screen can be the
 * one that is open when the link dies, and the screens WITHOUT a polling query
 * are precisely the ones that could never recover on their own.
 */
export function useBackendProbe(): void {
  const { backendReachable } = useOnlineAndReachable()

  useEffect(() => {
    if (backendReachable) return

    let cancelled = false
    const tick = () => {
      // Re-check rather than trusting the closure: a request from elsewhere may
      // have recovered reachability between two ticks, and the effect teardown
      // has not run yet.
      if (cancelled || isBackendReachable()) return
      void probeBackendOnce()
    }

    // Probe immediately as well as on the interval. The common case is a short
    // blip, and waiting a full interval to notice it is the behaviour this
    // module exists to remove.
    tick()
    const id = window.setInterval(tick, PROBE_INTERVAL_MS)
    return () => {
      cancelled = true
      window.clearInterval(id)
    }
  }, [backendReachable])
}
